import { JUMP_MS } from './frisbee-engine'

type Point = readonly [number, number]
export type LegPose = { knee: Point; paw: Point }
type LegFrame = readonly [number, number, number, number]

// Contact, weight transfer, push-off, fold, reach. Contact paws travel backwards.
const FRONT: LegFrame[] = [
  [22, -18, 34, -3],
  [21, -12, 21, -1],
  [10, -13, 3, -1],
  [1, -18, -10, -6],
  [5, -24, 0, -20],
  [16, -26, 18, -24],
  [27, -23, 34, -18],
  [30, -20, 41, -10],
]
const HIND: LegFrame[] = [
  [-30, -22, -44, -15],
  [-20, -25, -25, -23],
  [-11, -24, -6, -19],
  [-8, -17, 2, -8],
  [-14, -12, -9, -1],
  [-26, -14, -28, -1],
  [-34, -19, -43, -7],
  [-35, -24, -49, -13],
]
const mix = (a: number, b: number, t: number) => a + (b - a) * t
const smooth = (t: number) => t * t * (3 - 2 * t)
function sample(frames: number[], phase: number) {
  const position = (((phase % 1) + 1) % 1) * frames.length
  const index = Math.floor(position)
  const t = position - index
  const a = frames[(index + frames.length - 1) % frames.length]
  const b = frames[index]
  const c = frames[(index + 1) % frames.length]
  const d = frames[(index + 2) % frames.length]
  // Periodic Catmull–Rom keeps velocity continuous across all eight poses.
  return (
    0.5 *
    (2 * b +
      (c - a) * t +
      (2 * a - 5 * b + 4 * c - d) * t * t +
      (3 * b - a - 3 * c + d) * t * t * t)
  )
}
function leg(frames: LegFrame[], phase: number): LegPose {
  return {
    knee: [
      sample(
        frames.map((f) => f[0]),
        phase
      ),
      sample(
        frames.map((f) => f[1]),
        phase
      ),
    ],
    paw: [
      sample(
        frames.map((f) => f[2]),
        phase
      ),
      Math.min(
        -1,
        sample(
          frames.map((f) => f[3]),
          phase
        )
      ),
    ],
  }
}
function blend(a: LegPose, b: LegPose, t: number): LegPose {
  return {
    knee: [mix(a.knee[0], b.knee[0], t), mix(a.knee[1], b.knee[1], t)],
    paw: [mix(a.paw[0], b.paw[0], t), mix(a.paw[1], b.paw[1], t)],
  }
}

export function frisbeePetPose(at: number, jumpAt: number, reducedMotion = false) {
  const seconds = at / 1000
  // Integrated cadence: acceleration never restarts or jumps the animation phase.
  const cycle = 1.65 * seconds + 0.001 * seconds * seconds
  const standing = at === 0 || reducedMotion
  const phase = standing ? 0.17 : cycle % 1
  const sinceJump = at - jumpAt
  const airborne = sinceJump >= 0 && sinceJump < JUMP_MS
  const landing =
    jumpAt >= 0 && sinceJump >= JUMP_MS && sinceJump < JUMP_MS + 160
      ? Math.sin(((sinceJump - JUMP_MS) / 160) * Math.PI)
      : 0
  let front = leg(FRONT, phase)
  let farFront = leg(FRONT, phase + 0.13)
  let hind = leg(HIND, phase)
  let farHind = leg(HIND, phase + 0.14)
  let bodyY = standing ? 0 : sample([-2, 1, 2, -1, -4, -5, -4, -3], phase)
  let angle = standing ? 0 : sample([-3, 0, 3, 2, 0, -3, -4, -4], phase)
  if (standing) {
    front = { knee: [17, -13], paw: [21, -1] }
    farFront = { knee: [10, -14], paw: [12, -2] }
    hind = { knee: [-26, -15], paw: [-22, -1] }
    farHind = { knee: [-32, -16], paw: [-33, -2] }
  }
  if (airborne) {
    const progress = sinceJump / JUMP_MS
    const tuck = smooth(Math.min(1, progress / 0.25))
    const reach = smooth(Math.max(0, (progress - 0.64) / 0.36))
    front = blend(
      blend(
        { knee: [25, -23], paw: [36, -17] },
        { knee: [22, -21], paw: [12, -19] },
        tuck
      ),
      { knee: [23, -15], paw: [28, -1] },
      reach
    )
    hind = blend(
      blend(
        { knee: [-34, -19], paw: [-43, -7] },
        { knee: [-13, -25], paw: [-17, -17] },
        tuck
      ),
      { knee: [-25, -16], paw: [-30, -5] },
      reach
    )
    farFront = {
      knee: [front.knee[0] - 6, front.knee[1] - 2],
      paw: [front.paw[0] - 7, front.paw[1] - 3],
    }
    farHind = {
      knee: [hind.knee[0] - 4, hind.knee[1] - 2],
      paw: [hind.paw[0] - 7, hind.paw[1] - 3],
    }
    bodyY = -2
    angle = mix(-10, 8, smooth(progress))
    // Ease out of the actual take-off pose instead of snapping to a canned frame.
    const launchSeconds = jumpAt / 1000
    const launchPhase = (1.65 * launchSeconds + 0.001 * launchSeconds ** 2) % 1
    const launch = smooth(Math.min(1, sinceJump / 100))
    front = blend(leg(FRONT, launchPhase), front, launch)
    farFront = blend(leg(FRONT, launchPhase + 0.13), farFront, launch)
    hind = blend(leg(HIND, launchPhase), hind, launch)
    farHind = blend(leg(HIND, launchPhase + 0.14), farHind, launch)
  } else if (jumpAt >= 0 && sinceJump < JUMP_MS + 160 && !standing) {
    const recover = smooth((sinceJump - JUMP_MS) / 160)
    front = blend({ knee: [23, -15], paw: [28, -1] }, front, recover)
    farFront = blend({ knee: [17, -17], paw: [21, -4] }, farFront, recover)
    hind = blend({ knee: [-25, -16], paw: [-30, -5] }, hind, recover)
    farHind = blend({ knee: [-29, -18], paw: [-37, -8] }, farHind, recover)
    angle = mix(8, angle, recover)
    bodyY = mix(-2, bodyY, recover)
  }
  return {
    front,
    farFront,
    hind,
    farHind,
    bodyY: bodyY + landing * 3,
    angle,
    stretch:
      standing || airborne
        ? 1
        : 1 + sample([0.02, -0.02, -0.035, -0.01, 0.02, 0.045, 0.04, 0.03], phase),
    headAngle: standing
      ? 0
      : airborne
        ? -angle * 0.45
        : sample([2, -2, -4, -1, 3, 4, 3, 2], phase),
    ear: standing
      ? 0
      : airborne
        ? -17
        : sample([-18, -6, 11, 17, 5, -14, -25, -25], phase),
    tail: standing ? 0 : sample([7, 3, -5, -10, -4, 5, 13, 12], phase),
    tongue: standing ? 0 : sample([0, 1, 3, 2, 0, -1, -2, -1], phase),
    phase,
    airborne,
  }
}
